import React, { useState, useCallback } from 'react'
import ConnectingCanvas from './ConnectingCanvas'
import styled from 'styled-components'
import classnames from 'classnames'
import { useSelector } from 'react-redux'
import { getCurrentMapId, getVisitedMaps } from '../../store/selectors/jrPlusState/sensor'
import { getMainObjectiveFromMission, type Map } from '@mission.io/mission-toolkit'
import { NO_OBJECTIVE } from '@mission.io/mission-toolkit/constants'
import { getMissionState } from '../../store/stores/general'
import { Prompt } from '../../components/CreativeCanvas/components/Prompt'
import { PROGRESS } from '../../constants/styles'
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs'
import 'react-tabs/style/react-tabs.css'
import { FaStar } from 'react-icons/fa'
import type { VisitedMap } from './types'
import { useLineDrawingContext, getCardId } from './LineDrawingContext'
import { EmptyList } from '../../components/CollectedData/Container'
import { useContextualData } from '../../store/selectors/selectorHooks'
import { useMaps } from '../../store/stores/staticData'
import type { StudentSpecificContextualDataEntry } from '@mission.io/mission-toolkit'
import { ContextualDataCard } from '../../components/ContextualData/ContextualDataCard'
import type { OpenContext } from '../../components/LiteracyEvent/helperComponents/TextEventInput.jsx'
import { SCROLL_BAR_STYLES } from '../../components/CollectedData/Container.jsx'

type CollectedDataContext =
	| {|
			type: 'CULMINATING_MOMENT',
			culminatingMomentId: string,
	  |}
	| {|
			type: 'SENSOR_STATION',
			sensorStationMapId: string | null,
	  |}

/**
 * A data view for Sensor maps. Displays contextual data for all maps, with a tab for each map.
 * @param props.collectedDataContext The context where this data is being displayed. e.g. CULMINATING_MOMENT or SENSOR_STATION
 */
export default function CollectedData({
	className,
	collectedDataContext,
}: {
	className?: string,
	collectedDataContext: CollectedDataContext,
}): React$Node {
	const currentMapId: ?string = useSelector(getCurrentMapId)
	const visitedMaps: Array<VisitedMap> = useSelector(getVisitedMaps)
	const [tabIndex, setTabIndex] = useState(0)
	const { resetConnectingIds } = useLineDrawingContext()

	const handleScroll = useCallback(() => {
		resetConnectingIds && resetConnectingIds({ drawLines: false })
	}, [resetConnectingIds])

	const maps = useMaps()

	// build a tab for each map
	const tabs = []
	const tabPanels = []
	let tabIndexForCurrentMap = null
	for (let index = visitedMaps.length - 1; index >= 0; index--) {
		const map = visitedMaps[index]
		const fullMap = maps.find(m => m._id === map.id)
		if (!fullMap) {
			continue
		}
		if (currentMapId && map.id === currentMapId) {
			tabIndexForCurrentMap = tabs.length
		}
		tabs.push(
			<Tab
				key={map.id}
				selectedClassName="selected"
				className={classnames(
					'select-none flex items-center px-2 py-1 text-base cursor-pointer font-[Orbitron,sans-serif] mx-2 -ml-1 overflow-hidden bg-[#192b5e] text-white rounded-t-[var(--spacing)]',
					'focus:bg-primary-700 focus:outline-none',
					'[&.selected]:bg-primary-700 [&.selected]:outline-none [&.selected]:text-white'
				)}>
				{map.id === currentMapId && <Star />}
				{map.name}
			</Tab>
		)
		tabPanels.push(
			<TabPanelWithScrollStyles
				key={map.id}
				onScroll={handleScroll}
				className={`hidden [&.is-selected]:block p-5 bg-primary-700 rounded-b-lg rounded-tr-lg h-full w-full overflow-y-auto overflow-x-hidden `}>
				<div className="flex flex-col gap-3">
					<ContextualDataForMap
						{...{
							map: fullMap,
							includeUnscannedObjects: collectedDataContext.type === 'CULMINATING_MOMENT',
							onHover: contextualData => {
								if (contextualData.dataSource.mapObjectId) {
									resetConnectingIds?.({
										drawLines: true,
										mapObjectId: contextualData.dataSource.mapObjectId,
										cardId: getCardId(contextualData.id),
									})
								} else {
									resetConnectingIds?.({
										drawLines: false,
									})
								}
							},
							onBlur: () => {
								resetConnectingIds?.({
									drawLines: false,
								})
							},
							openContext: {
								...collectedDataContext,
								mapId: fullMap._id,
							},
						}}
					/>
				</div>
			</TabPanelWithScrollStyles>
		)
	}

	const mainObj = useSelector(state => getMainObjectiveFromMission(getMissionState(state)))
	// Only show the objective if it exists and it is NOT the default, "no current objective"
	const showObjective = mainObj?.objectiveText && mainObj.objectiveText !== NO_OBJECTIVE

	return (
		<>
			<ConnectingCanvas />
			<div
				className={classnames(
					'collectedData h-full flex gap-4 flex-col overflow-x-hidden relative overflow-y-hidden z-0 flex-grow',
					className
				)}>
				{showObjective ? <Prompt content={`Objective: ${mainObj?.objectiveText}`} /> : null}
				{tabs.length > 0 && tabPanels.length > 0 ? (
					<div className="flex flex-col flex-1 min-h-0">
						{tabIndexForCurrentMap === tabIndex && tabs.length > 1 && (
							<div className="top-0 font-[Orbitron,sans-serif] tracking-wider text-base text-left py-0 pb-2.5 pl-5 text-white">
								Current Location
							</div>
						)}
						<div className="flex-1 min-h-0">
							<Tabs
								className="h-full min-h-0 flex flex-col"
								selectedTabPanelClassName="is-selected"
								selectedIndex={tabIndex}
								onSelect={selectedTabIndex => setTabIndex(selectedTabIndex)}>
								<TabList className="list-none pt-1 pr-1 pl-1 pb-0 flex m-0">{tabs}</TabList>
								<div className={`flex-1 min-h-0`}>{tabPanels}</div>
							</Tabs>
						</div>
					</div>
				) : (
					<EmptyList>No Scanned Data Results</EmptyList>
				)}
			</div>
		</>
	)
}

/**
 * Displays all contextual data for a given map
 *
 * @param props
 * @param props.map - The map to display contextual data for
 * @param props.includeUnscannedObjects - Whether or not to include data from unscanned objects
 * @param props.onHover - Called when a card is hovered over
 * @param props.onBlur - Called when a card is no longer hovered over
 * @param props.openContext - The openContext for the data in this map
 *
 */
function ContextualDataForMap({
	map,
	includeUnscannedObjects,
	onHover,
	onBlur,
	openContext,
}: {
	map: Map,
	includeUnscannedObjects: boolean,
	onHover: (contextualData: StudentSpecificContextualDataEntry) => mixed,
	onBlur: () => void,
	openContext: OpenContext,
}): React$Node {
	const contextualData = useContextualData({
		type: 'MAP',
		map,
		includeUnscannedObjects,
	})

	return contextualData.length > 0 ? (
		contextualData.map((contextualData: StudentSpecificContextualDataEntry, index) => {
			return (
				<ContextualDataCard
					{...{
						key: contextualData.id,
						lookup: {
							type: 'MAP',
							map,
							includeUnscannedObjects,
							id: contextualData.id,
						},
						contextualData,
						id: getCardId(contextualData.id),
						onHover: () => {
							onHover(contextualData)
						},
						onBlur,
						openContext,
					}}
				/>
			)
		})
	) : (
		<EmptyList>No Scanned Data Results</EmptyList>
	)
}

const Star = styled(FaStar)`
	color: ${PROGRESS};
	width: 15px;
	height: 15px;
	margin: 0 5px 5px 0;
`

const TabPanelWithScrollStyles = styled(TabPanel)`
	${SCROLL_BAR_STYLES}
`
